import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/firebaseConfig';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import './LimitExceededModal.css';

/**
 * LimitExceededModal
 * 
 * Shown when a user's download/ATS/cover-letter limit is exceeded.
 * Two variants:
 *   A) Add-on eligible (config=true OR silver template) → Buy button + Pricing link
 *   B) Not eligible (config=false AND gold/platinum) → Browse Silver + Pricing link
 *
 * Props:
 *   isOpen        - boolean
 *   onClose       - () => void
 *   onBuyAddon    - (addonData) => void  — triggers Razorpay purchase
 *   limitType     - 'download' | 'ats' | 'coverLetter'
 *   templateTier  - 'silver' | 'gold' | 'platinum'
 *   resumeState   - { resumeData, templateHTML, colorTheme, fontFamily, hiddenSections, resumeId } (for carrying data to silver template)
 */
const LimitExceededModal = ({ isOpen, onClose, onBuyAddon, limitType = 'download', templateTier = 'silver', resumeState = null }) => {
  const navigate = useNavigate();
  const [addonData, setAddonData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purchasing, setPurchasing] = useState(false);

  // Reset purchasing state when modal opens fresh
  useEffect(() => {
    if (isOpen) {
      setPurchasing(false);
    }
  }, [isOpen]);

  // Config: whether addon is eligible for all templates or silver only
  const addonEligibleAll = import.meta.env.VITE_ADDON_ELIGIBLE_ALL_TEMPLATES === 'true';

  // Determine if this user can purchase addon for current template
  const isAddonEligible = addonEligibleAll || (templateTier || '').toLowerCase() === 'silver';

  // Limit type labels
  const limitLabels = {
    download: 'Resume Download',
    ats: 'ATS Score Check',
    coverLetter: 'Cover Letter Download'
  };

  const limitLabel = limitLabels[limitType] || 'Download';

  // Fetch addon pricing from Firestore
  useEffect(() => {
    if (!isOpen || addonData) return;

    const fetchAddonData = async () => {
      try {
        setLoading(true);
        // Try named document first
        const addonDoc = await getDoc(doc(db, 'plans', 'singleDownloadAddon'));
        if (addonDoc.exists()) {
          const data = addonDoc.data();
          setAddonData({
            id: addonDoc.id,
            initialPrice: data.initialPrice || data.initial_price || null,
            actualPrice: data.actualPrice || data.actual_price || 20,
            currency: data.currency || 'INR',
            name: data.planName || data.name || 'Single Template Download',
            description: data.description || 'Download 1 additional template',
            validForAllTemplates: data.validForAllTemplates ?? false,
          });
        } else {
          // Fallback: query by name field
          const q = query(collection(db, 'plans'), where('name', '==', 'addOn'));
          const snap = await getDocs(q);
          if (!snap.empty) {
            const d = snap.docs[0];
            const data = d.data();
            setAddonData({
              id: d.id,
              initialPrice: data.initialPrice || data.initial_price || null,
              actualPrice: data.actualPrice || data.actual_price || 20,
              currency: data.currency || 'INR',
              name: data.planName || data.name || 'Single Template Download',
              description: data.description || 'Download 1 additional template',
              validForAllTemplates: data.validForAllTemplates ?? false,
            });
          } else {
            // Hardcoded fallback
            setAddonData({
              id: 'singleDownloadAddon',
              initialPrice: 29,
              actualPrice: 20,
              currency: 'INR',
              name: 'Single Template Download',
              description: 'Download 1 additional template',
              validForAllTemplates: false,
            });
          }
        }
      } catch (error) {
        console.error('Error fetching addon data:', error);
        setAddonData({
          id: 'singleDownloadAddon',
          initialPrice: 29,
          actualPrice: 20,
          currency: 'INR',
          name: 'Single Template Download',
          description: 'Download 1 additional template',
          validForAllTemplates: false,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAddonData();
  }, [isOpen, addonData]);

  if (!isOpen) return null;

  const handleBuyAddon = async () => {
    if (!addonData || purchasing) return;
    setPurchasing(true);
    try {
      await onBuyAddon(addonData);
    } catch (err) {
      console.error('Addon purchase error:', err);
      setPurchasing(false);
    }
  };

  const handleBrowseSilver = () => {
    // Save current resume data to sessionStorage so it can be pre-filled in the new silver template
    if (resumeState?.resumeData) {
      sessionStorage.setItem('addon_browse_resume_data', JSON.stringify(resumeState.resumeData));
    }
    onClose();
    navigate('/templates', { state: { addonMode: true, filterTier: 'silver', resumeData: resumeState?.resumeData || null } });
  };

  const handleExplorePlans = () => {
    onClose();
    navigate('/pricing');
  };

  return (
    <div className="limit-modal-overlay" onClick={onClose}>
      <div className="limit-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="limit-modal-close" onClick={onClose} aria-label="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Header */}
        <div className="limit-modal-header">
          <div className="limit-modal-icon-wrap">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <h2 className="limit-modal-title">{limitLabel} Limit Reached</h2>
          <p className="limit-modal-desc">
            {isAddonEligible
              ? `You have used all available ${limitLabel.toLowerCase()}s in your current plan. Purchase a one-time add-on to proceed instantly.`
              : `You have used all available ${limitLabel.toLowerCase()}s. Add-on purchases are available for Silver templates at a discounted price.`
            }
          </p>
        </div>

        {/* Divider */}
        <div className="limit-modal-divider" />

        {/* Body */}
        <div className="limit-modal-body">
          {isAddonEligible ? (
            /* ─── Variant A: Buy Add-On ─── */
            <div className="limit-addon-card">
              <div className="limit-addon-badge">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span>Instant Access</span>
              </div>

              <div className="limit-addon-info">
                <h3 className="limit-addon-name">
                  {loading ? 'Loading...' : (addonData?.name || 'Single Template Download')}
                </h3>
                <p className="limit-addon-desc">
                  {addonData?.description || 'Get +1 extra download for this template'}
                </p>
              </div>

              {loading ? (
                <div className="limit-addon-price-skeleton">
                  <div className="limit-skeleton-bar" />
                </div>
              ) : addonData ? (
                <div className="limit-addon-pricing">
                  {addonData.initialPrice && Number(addonData.initialPrice) !== Number(addonData.actualPrice) && (
                    <span className="limit-addon-original">
                      <span className="limit-currency-sm">Rs.</span>{addonData.initialPrice}
                    </span>
                  )}
                  <span className="limit-addon-price">
                    <span className="limit-currency">Rs.</span>
                    <span className="limit-amount">{addonData.actualPrice}</span>
                  </span>
                </div>
              ) : null}

              <button
                className="limit-buy-btn"
                onClick={handleBuyAddon}
                disabled={loading || purchasing || !addonData}
              >
                {purchasing ? (
                  <>
                    <svg className="limit-spinner" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                      <line x1="1" y1="10" x2="23" y2="10" />
                    </svg>
                    Buy This Template — Rs.{addonData?.actualPrice || '20'}
                  </>
                )}
              </button>
            </div>
          ) : (
            /* ─── Variant B: Browse Silver Templates ─── */
            <div className="limit-addon-card limit-addon-card--alt">
              <div className="limit-addon-badge limit-addon-badge--info">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                <span>Silver Templates Only</span>
              </div>

              <div className="limit-addon-info">
                <h3 className="limit-addon-name">Add-on Available for Silver Templates</h3>
                <p className="limit-addon-desc">
                  Pick any Silver template and download it for just Rs.{addonData?.actualPrice || '20'} — no plan upgrade needed.
                </p>
              </div>

              <button className="limit-browse-btn" onClick={handleBrowseSilver}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                </svg>
                Browse Silver Templates — Rs.{addonData?.actualPrice || '20'} each
              </button>
            </div>
          )}

          {/* Secondary: Explore Plans */}
          <button className="limit-plans-btn" onClick={handleExplorePlans}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
            </svg>
            Explore Plans for Unlimited Downloads
          </button>
        </div>

        {/* Footer note */}
        <p className="limit-modal-footer-note">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          Secure payment via Razorpay. Instant download after purchase.
        </p>
      </div>
    </div>
  );
};

export default LimitExceededModal;
